import React from 'react';
import {useForm, Controller} from 'react-hook-form';
import {
  Button,
  Grid,
  TextField,
  Typography,
  CircularProgress,
} from '@material-ui/core';
import {ISupplierCompanyProfile} from 'constants/interfaces';

export function FdCompanyForm(props: {
  company?: ISupplierCompanyProfile;
  isLoading: boolean;
  submitButtonTitle: string;
  onSubmit: (company: Partial<ISupplierCompanyProfile>) => any;
  cancelButtonTitle?: string;
  onCancel?: () => any;
  readonly?: boolean;
  disableUnsupportedFields?: boolean;
}) {
  const {handleSubmit, control} = useForm<Partial<ISupplierCompanyProfile>>({
    defaultValues: {
      ...props.company,
    },
  });

  const onSubmit = (data: Partial<ISupplierCompanyProfile>) => {
    props.onSubmit(data);
  };

  return (
    <div className="generic-form-container fd-company-form">
      <div className="form-container">
        <form className="form" onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2} className="form-section">
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Company Information
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="name"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="Company Name"
                    variant="outlined"
                    fullWidth
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.readonly || props.isLoading}
                  />
                )}
                rules={{
                  required: 'Company name is required',
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="tradingName"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="Trading Name"
                    variant="outlined"
                    fullWidth
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={
                      error
                        ? error.message
                        : props.disableUnsupportedFields
                        ? 'Read-only in this flow.'
                        : null
                    }
                    disabled={
                      props.readonly ||
                      props.isLoading ||
                      props.disableUnsupportedFields
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="vatNumber"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="VAT Number"
                    variant="outlined"
                    fullWidth
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={
                      error
                        ? error.message
                        : props.disableUnsupportedFields
                        ? 'Read-only in this flow.'
                        : null
                    }
                    disabled={
                      props.readonly ||
                      props.isLoading ||
                      props.disableUnsupportedFields
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="taxNumber"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="Tax Number"
                    variant="outlined"
                    fullWidth
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={
                      error
                        ? error.message
                        : props.disableUnsupportedFields
                        ? 'Read-only in this flow.'
                        : null
                    }
                    disabled={
                      props.readonly ||
                      props.isLoading ||
                      props.disableUnsupportedFields
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Address Details
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="addressLine1"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="Address Line 1"
                    variant="outlined"
                    fullWidth
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.readonly || props.isLoading}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="addressLine2"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="Address Line 2"
                    variant="outlined"
                    fullWidth
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.readonly || props.isLoading}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="city"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="City"
                    variant="outlined"
                    fullWidth
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.readonly || props.isLoading}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="province"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="Province"
                    variant="outlined"
                    fullWidth
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.readonly || props.isLoading}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="areaCode"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="Area Code"
                    variant="outlined"
                    fullWidth
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.readonly || props.isLoading}
                  />
                )}
              />
            </Grid>
          </Grid>
          {!props.readonly && (
            <Grid container spacing={2} style={{marginTop: '1rem'}}>
              <Grid item xs={12} sm={6}>
                {props.cancelButtonTitle && (
                  <Button
                    className="secondary-btn"
                    variant="contained"
                    fullWidth
                    disableElevation
                    disabled={props.isLoading}
                    onClick={props.onCancel}
                  >
                    {props.cancelButtonTitle}
                  </Button>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  className="primary-btn"
                  type="submit"
                  variant="contained"
                  fullWidth
                  disableElevation
                  color="primary"
                  disabled={props.isLoading}
                >
                  {props.isLoading ? (
                    <CircularProgress color="primary" size={24} thickness={6} />
                  ) : (
                    props.submitButtonTitle
                  )}
                </Button>
              </Grid>
            </Grid>
          )}
        </form>
      </div>
    </div>
  );
}
